import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { ACCESS_OVERLAY_LABELS } from '../../shared/index.js';

import { ApiError } from '../../api/client.js';
import type { ManagedUserDetail } from '../../api/endpoints.js';
import { userApi } from '../../api/endpoints.js';
import { queryKeys } from '../../app/query-client.js';
import { useSession } from '../../auth/session.js';
import { HeaderCell, TableHeaderRow, TableShell, tableRowClass } from '../../components/data-table.js';
import { PageHeader } from '../../components/page-header.js';
import { Select } from '../../components/select.js';
import { Alert, Badge, Button, CONTROL_FIELD, Spinner } from '../../components/ui.js';
import { useDepartments, useJobRoles } from '../../hooks/use-directory.js';
import { CreatePerson, EditPerson } from './users-editor.js';

const ANY = 'any';
const PAGE_SIZE = 25;

export function UsersPage(): ReactNode {
  const queryClient = useQueryClient();
  const { principal } = useSession();
  const { departments } = useDepartments();
  const { jobRoles } = useJobRoles();

  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>(ANY);
  const [activeFilter, setActiveFilter] = useState<string>('true');
  const [page, setPage] = useState(1);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const missing =
    departments.length === 0
      ? 'Create a department first — every account needs one.'
      : jobRoles.length === 0
        ? 'Create a position first — every account needs one.'
        : null;

  const filters = {
    ...(search.trim() === '' ? {} : { search: search.trim() }),
    ...(departmentFilter === ANY ? {} : { departmentId: departmentFilter }),
    ...(activeFilter === ANY ? {} : { isActive: activeFilter as 'true' | 'false' }),
    page,
    pageSize: PAGE_SIZE,
  };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.users.list(filters),
    queryFn: () => userApi.list(filters),
  });

  const { data: selected } = useQuery({
    queryKey: queryKeys.users.detail(selectedId ?? 'none'),
    queryFn: () => userApi.get(selectedId ?? ''),
    enabled: selectedId !== null,
  });

  const fail = (fallback: string) => (error: unknown) => {
    setSaved(null);
    setProblem(error instanceof ApiError ? error.message : fallback);
  };

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.users.lists });
    await queryClient.invalidateQueries({ queryKey: queryKeys.org.users });
  };

  const done = async (message: string, updated: ManagedUserDetail | null): Promise<void> => {
    setProblem(null);
    setSaved(message);

    if (updated !== null) {
      setSelectedId(updated.id);
      queryClient.setQueryData(queryKeys.users.detail(updated.id), updated);
    }

    await refresh();
  };

  return (
    <>
      <PageHeader
        title="People"
        subtitle="Accounts, their position, and which departments they can reach."
      >
        <Button
          type="button"
          size="sm"
          className="ml-auto"
          disabled={missing !== null}
          {...(missing === null ? {} : { title: missing })}
          onClick={() => {
            setCreating(true);
            setSelectedId(null);
            setProblem(null);
            setSaved(null);
          }}
        >
          Add person
        </Button>
      </PageHeader>

      <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-7 sm:py-6">
        {problem === null ? null : <Alert tone="error">{problem}</Alert>}
        {saved === null ? null : <Alert tone="success">{saved}</Alert>}
        {missing === null ? null : <Alert tone="info">{missing}</Alert>}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search by name or email"
            className={`${CONTROL_FIELD} min-w-[220px] flex-1`}
          />

          <Select
            label="Department filter"
            value={departmentFilter}
            onChange={(next) => {
              setDepartmentFilter(next);
              setPage(1);
            }}
            options={[
              { value: ANY, label: 'Any department' },
              ...departments.map((entry) => ({ value: entry.id, label: entry.name })),
            ]}
          />

          <Select
            label="Account status"
            value={activeFilter}
            onChange={(next) => {
              setActiveFilter(next);
              setPage(1);
            }}
            options={[
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Deactivated' },
              { value: ANY, label: 'Everyone' },
            ]}
          />
        </div>

        {isLoading ? (
          <div className="grid place-items-center py-20 text-muted">
            <Spinner className="size-5" />
          </div>
        ) : (
          <>
            <TableShell>
              <TableHeaderRow>
                <HeaderCell className="min-w-[120px] max-w-[28rem] flex-1">Person</HeaderCell>
                <HeaderCell className="hidden min-w-[110px] max-w-[32rem] flex-1 md:block">
                  Position
                </HeaderCell>
                <HeaderCell className="hidden w-40 shrink-0 lg:block">Department</HeaderCell>
                <HeaderCell className="w-[168px] shrink-0 text-right">Access</HeaderCell>
              </TableHeaderRow>

              {(data?.items ?? []).map((person, index) => (
                <button
                  key={person.id}
                  type="button"
                  onClick={() => {
                    setSelectedId(person.id);
                    setCreating(false);
                    setProblem(null);
                    setSaved(null);
                  }}
                  className={tableRowClass(
                    index === 0,
                    selectedId === person.id ? 'bg-mist/70 dark:bg-white/5' : '',
                  )}
                >
                  <span className="min-w-[120px] max-w-[28rem] flex-1">
                    <span className="block truncate text-[14px] font-semibold text-navy dark:text-dark-ink">
                      {person.firstName} {person.lastName}
                      {person.isActive ? null : (
                        <>
                          {' '}
                          <Badge tone="neutral">Deactivated</Badge>
                        </>
                      )}
                    </span>
                    <span className="block truncate text-[12.5px] text-muted">{person.email}</span>
                  </span>

                  <span className="hidden min-w-[110px] max-w-[32rem] flex-1 truncate text-[12.5px] text-muted md:block">
                    {person.jobRoleName}
                  </span>

                  <span className="hidden w-40 shrink-0 truncate text-[12.5px] text-muted lg:block">
                    {person.homeDepartmentName ?? '—'}
                  </span>

                  <span className="flex w-[168px] shrink-0 flex-wrap justify-end gap-1">
                    {person.overlays.map((overlay) => (
                      <Badge key={overlay} tone="cyan">
                        {ACCESS_OVERLAY_LABELS[overlay]}
                      </Badge>
                    ))}
                  </span>
                </button>
              ))}

              {(data?.items ?? []).length === 0 ? (
                <p className="px-4 py-10 text-center text-[13.5px] text-muted">
                  Nobody matches that.
                </p>
              ) : null}
            </TableShell>

            {data !== undefined && data.meta.totalPages > 1 ? (
              <div className="mt-3 flex items-center justify-end gap-2">
                <span className="text-[12.5px] text-muted">
                  Page {data.meta.page} of {data.meta.totalPages}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => {
                    setPage((current) => current - 1);
                  }}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={page >= data.meta.totalPages}
                  onClick={() => {
                    setPage((current) => current + 1);
                  }}
                >
                  Next
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>

      {creating ? (
        <CreatePerson
          departments={departments}
          jobRoles={jobRoles}
          onClose={() => {
            setCreating(false);
          }}
          onFail={fail('That account could not be created.')}
          onCreated={async (person) => {
            setCreating(false);
            await done(`${person.firstName} ${person.lastName} can now sign in.`, person);
          }}
        />
      ) : null}

      {selected === undefined || selectedId === null ? null : (
        <EditPerson
          key={selected.id}
          person={selected}
          departments={departments}
          jobRoles={jobRoles}
          isSelf={selected.id === principal?.id}
          onClose={() => {
            setSelectedId(null);
          }}
          onFail={fail}
          onDone={done}
        />
      )}
    </>
  );
}
